// Operação do orçamento — reaproveita 1:1 o vocabulário visual de
// `service_order/Operator.js` (header gradiente `os-header`, carrinho estilo
// POS, DESIGN-SYSTEM.md §6.3), adaptado ao domínio de orçamento: sem abas
// Financeiro/Checklist (só a lista de itens) e com um menu de troca de
// status no lugar das ações fixas de status. Ref: `prototipo-operacao-orcamento.html`.
import {
    ArrowLeft,
    ArrowLeftRight,
    Check,
    Clock,
    Download,
    FileText,
    Layers,
    Link2,
    Mail,
    Minus,
    Package,
    Pencil,
    Plus,
    Send,
    Tag,
    Trash2,
    Wrench,
} from "lucide-react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
    Stack,
    CircularProgress,
    Menu,
    MenuItem,
    TextField,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Button,
    Skeleton
} from "@mui/material";
import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { httpClient } from "../../common/httpclient";
import DefaultAutocomplete from "../../component/autocomplete/DefaultAutocomplete";
import { standaloneFieldSx } from "../../common/fieldStyles";
import PageScaffold from "../../component/pageScaffold/PageScaffold";
import DialogForm from "../../component/dialog/DialogForm";
import QuoteWhatsappDialog, { useWhatsappConnected } from "../../component/dialog/QuoteWhatsappDialog";
import { usePermissions } from "../../hooks/usePermissions";
import { useIsMobileShell } from "../../hooks/useIsMobileShell";
import MobileQuoteOperator from "../../component/servicequote/MobileQuoteOperator";
import EditQuoteModal from "./EditQuoteModal";
import StatusChip from "../../component/common/StatusChip";
import dayjs from 'dayjs';
import HeaderStatusSteps from "../../component/common/HeaderStatusSteps";
import { orderMoment } from "../../component/serviceorder/orderMobileUtils";
import { formatCurrency, formatPhone } from "../../common/formatters";
import html2pdf from 'html2pdf.js';
import { useEnterpriseConfigContext } from "../../component/context/EnterpriseConfigContext";
import { messageFromApiErrorBody } from "../../common/apiErrorMessage";

// ============================================================================
// Componentes locais de apresentação — mesma duplicação intencional de
// `service_order/Operator.js` (cada tela mantém sua própria cópia; ver
// comentário lá). Vocabulário: `.os-header` (gradiente), `.os-actions`
// (botões translúcidos), `.os-meta-grid` (label/valor), `.pos-item`
// (carrinho), `.status-menu` (dropdown de troca de status).
// ============================================================================

// 5 status reais de `StatusChip domain="serviceQuote"` (DESIGN-SYSTEM.md §4),
// aqui com tom+ícone próprios para o pill do cabeçalho (com dropdown) e o
// menu de troca de status — mesmo mapeamento de cor/rótulo de
// `prototipo-lista-orcamentos.html`/`prototipo-operacao-orcamento.html`.
const STATUS_ORDER = ['novo', 'enviado para cliente', 'aguardando aprovacao', 'aprovado', 'ordem gerada'];
const STATUS_META = {
    'novo': { label: 'Novo', tone: 'neutral', icon: FileText },
    'enviado para cliente': { label: 'Enviado', tone: 'info', icon: Send },
    'aguardando aprovacao': { label: 'Aguardando Aprovação', tone: 'warning', icon: Clock },
    'aprovado': { label: 'Aprovado', tone: 'success', icon: Check },
    'ordem gerada': { label: 'Ordem Gerada', tone: 'purple', icon: ArrowLeftRight },
};
function toneDotColor(tone) {
    switch (tone) {
        case 'success': return 'success.main';
        case 'warning': return 'warning.main';
        case 'info': return 'primary.main';
        case 'purple': return 'secondary.main';
        default: return 'text.disabled';
    }
}
// Mesmas regras de correspondência de `isConverted()`/`getStatusConfig()`
// (código anterior desta tela) — só normaliza para uma das 5 chaves de
// `STATUS_META` a partir do valor cru retornado pela API.
function statusKeyOf(status) {
    const s = (status || '').toLowerCase();
    if (s.includes('ordem gerada') || s.includes('convertido')) return 'ordem gerada';
    if (s.includes('aguardando')) return 'aguardando aprovacao';
    if (s.includes('aprovado')) return 'aprovado';
    if (s.includes('enviado') || s.includes('cliente')) return 'enviado para cliente';
    return 'novo';
}

// `.pill.status-menu-trigger` — pill translúcido clicável que abre o menu de
// troca de status (§6.3, adaptado com dropdown — extensão em relação ao
// `HeaderStatusPill` estático de `service_order/Operator.js`).
function StatusMenuTrigger({ statusKey, onClick, disabled }) {
    const meta = STATUS_META[statusKey];
    const Icon = meta.icon;
    return (
        <Box
            component="button"
            type="button"
            onClick={disabled ? undefined : onClick}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '11.5px',
                fontWeight: 700,
                padding: '5px 9px 5px 11px',
                borderRadius: '20px',
                border: 'none',
                bgcolor: 'rgba(255,255,255,.22)',
                color: '#fff',
                cursor: disabled ? 'default' : 'pointer',
                fontFamily: 'inherit',
            }}
        >
            <Icon size={13} strokeWidth={2} />
            {meta.label}
            {!disabled && <ArrowLeft size={12} strokeWidth={2.5} style={{ transform: 'rotate(-90deg)', marginLeft: 2 }} />}
        </Box>
    );
}

// `.os-actions button` — ghost translúcido sobre o gradiente; `solid` = ação
// primária (fundo branco), `danger` = hover destrutivo — mesmo componente de
// `service_order/Operator.js`.
function HeaderActionButton({ icon: Icon, label, onClick, solid, danger, loading, disabled }) {
    return (
        <Button
            onClick={onClick}
            disabled={disabled}
            startIcon={loading ? <CircularProgress size={13} sx={{ color: 'inherit' }} /> : <Icon size={14} strokeWidth={2} />}
            sx={{
                padding: '9px 14px',
                borderRadius: '10px',
                border: '1px solid rgba(255,255,255,.3)',
                bgcolor: solid ? '#fff' : 'rgba(255,255,255,.12)',
                color: solid ? 'brand2.main' : '#fff',
                fontSize: '12.5px',
                fontWeight: 700,
                '&:hover': solid
                    ? { bgcolor: '#fff', filter: 'brightness(.97)' }
                    : danger
                        ? { bgcolor: '#fecaca', color: '#7f1d1d', borderColor: '#fecaca' }
                        : { bgcolor: 'rgba(255,255,255,.22)', borderColor: 'rgba(255,255,255,.3)' },
                '&.Mui-disabled': { color: solid ? 'brand2.main' : 'rgba(255,255,255,.6)', opacity: 0.7 },
            }}
        >
            {label}
        </Button>
    );
}

// `.os-meta-grid .mi` — par label/valor (+ sub-linha opcional).
function MetaItem({ label, value, sub }) {
    if (!value) return null;
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '10.5px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '.05em', mb: '4px', color: '#fff' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: '#fff' }}>{value}</Typography>
            {sub && <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,.75)', mt: '2px' }}>{sub}</Typography>}
        </Box>
    );
}

// `.summary-row` — resumo lateral sticky.
function SummaryRow({ label, value, tone, big }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.875 }}>
            <Typography sx={{ fontSize: 13, color: tone === 'danger' ? 'error.main' : 'text.secondary' }}>{label}</Typography>
            <Typography sx={{
                fontSize: big ? 17 : 14,
                fontWeight: big ? 800 : 700,
                color: tone === 'danger' ? 'error.main' : tone === 'success' ? 'success.main' : 'text.primary',
            }}>
                {value}
            </Typography>
        </Box>
    );
}

// `.items-empty` — dashed-circle + texto (§5.11).
function EmptyMiniState({ icon: Icon, title, subtitle }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 5, textAlign: 'center' }}>
            <Box sx={{
                width: 70,
                height: 70,
                borderRadius: '50%',
                border: '2px dashed',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.disabled',
            }}>
                <Icon size={26} strokeWidth={1.6} />
            </Box>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.secondary' }}>{title}</Typography>
            {subtitle && <Typography sx={{ fontSize: '12px', color: 'text.disabled', maxWidth: 280 }}>{subtitle}</Typography>}
        </Box>
    );
}

// Botão redondo +/- do carrinho (`.qty-box button`). O bloco `&.Mui-disabled`
// precisa repetir o `bgcolor` — o `styleOverrides` interno do `IconButton`
// do MUI zera o background em `.Mui-disabled` com especificidade maior que
// o `bgcolor` "base" do `sx`, senão o botão vira invisível quando desabilitado
// (ex.: "−" com `quantity <= 1`).
function QtyButton({ tone, onClick, disabled, loading, children, compact }) {
    const bg = tone === 'minus' ? 'error.main' : 'success.main';
    return (
        <IconButton
            size="small"
            onClick={onClick}
            disabled={disabled}
            sx={{
                width: compact ? 30 : 26,
                height: compact ? 30 : 26,
                // `&&` vence o `min-width/min-height: 44px` global do MobileLayout.
                ...(compact && { '&&': { minWidth: 30, minHeight: 30 } }),
                borderRadius: compact ? '50%' : '8px',
                color: '#fff',
                bgcolor: bg,
                '&:hover': { bgcolor: tone === 'minus' ? 'error.dark' : 'success.dark' },
                '&.Mui-disabled': { opacity: 0.5, color: '#fff', bgcolor: bg },
            }}
        >
            {loading ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : children}
        </IconButton>
    );
}

// Botão de ícone de linha (desconto/remover) — `.item-btns button`.
function RowIconButton({ onClick, disabled, loading, hoverColor, children, compact }) {
    return (
        <IconButton
            size="small"
            onClick={onClick}
            disabled={disabled}
            sx={{
                width: compact ? 32 : 28,
                height: compact ? 32 : 28,
                // `&&` vence o `min-width/min-height: 44px` global do MobileLayout.
                ...(compact && { '&&': { minWidth: 32, minHeight: 32 } }),
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'divider',
                color: compact && hoverColor === 'error.main' ? 'error.main' : 'text.disabled',
                '&:hover': { color: hoverColor || 'primary.main', borderColor: hoverColor || 'primary.main' },
            }}
        >
            {loading ? <CircularProgress size={12} /> : children}
        </IconButton>
    );
}

// `.pos-item` — linha do carrinho de itens do orçamento; editável ou
// somente leitura conforme `editable` (trava quando `status = Convertido`).
// Sem botão de editar item — o orçamento nunca teve essa ação (diferente de
// OS), então não foi inventada aqui.
function CartRow({ item, editable, onIncrement, onDecrement, onDiscount, onRemove, incrementing, decrementing, deleting }) {
    const isService = (item.type_item || item.type) === 'service';
    const Icon = isService ? Wrench : Package;
    const itemTitle = item.item_name || item.name;
    const unitDisplay = item.amount_unit ?? '0,00';
    const totalDisplay = item.amount_total ?? item.total ?? '0,00';
    const compact = useIsMobileShell();
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: compact ? 1 : 1.5,
            p: compact ? '8px 10px' : '13px 10px',
            borderRadius: '12px',
            mb: 0.75,
            border: '1px solid',
            borderColor: 'divider',
            flexWrap: 'wrap',
            '&:hover': { bgcolor: 'background.subtle' },
        }}>
            <Box sx={{
                width: compact ? 32 : 42,
                height: compact ? 32 : 42,
                borderRadius: compact ? '9px' : '11px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isService ? 'secondary.bg' : 'info.bg',
                color: isService ? 'secondary.main' : 'primary.main',
            }}>
                <Icon size={compact ? 15 : 18} strokeWidth={1.8} />
            </Box>

            {/* No mobile o título ocupa a 1ª linha e os controles ficam na 2ª. */}
            <Box sx={{ flex: compact ? '1 1 calc(100% - 50px)' : 1, minWidth: compact ? 0 : 160 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: 'text.primary' }}>{itemTitle}</Typography>
                    <StatusChip domain="itemType" value={isService ? 'servico' : 'produto'} />
                </Box>
                <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: '2px' }}>
                    R$ {unitDisplay} · un.
                </Typography>
                {!isService && item.item_discount_type ? (
                    <Typography sx={{ fontSize: 11, color: 'error.main', mt: '2px' }}>
                        Desconto: {item.item_discount_type}
                        {item.item_discount_amount ? ` (− R$ ${item.item_discount_amount})` : ''}
                    </Typography>
                ) : null}
            </Box>

            {editable ? (
                <>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        flexShrink: 0,
                        ...(compact && { gap: 0.25, p: '2px', border: '1px solid', borderColor: 'divider', borderRadius: '999px' }),
                    }}>
                        <QtyButton compact={compact} tone="minus" onClick={onDecrement} disabled={decrementing || item.quantity <= 1} loading={decrementing}>
                            <Minus size={compact ? 14 : 13} strokeWidth={2.5} />
                        </QtyButton>
                        <Typography sx={{ width: compact ? 26 : 22, textAlign: 'center', fontWeight: 800, fontSize: 13 }}>
                            {item.quantity}
                        </Typography>
                        <QtyButton compact={compact} tone="plus" onClick={onIncrement} disabled={incrementing} loading={incrementing}>
                            <Plus size={compact ? 14 : 13} strokeWidth={2.5} />
                        </QtyButton>
                    </Box>

                    <Typography sx={{
                        width: 84,
                        textAlign: 'right',
                        fontWeight: 800,
                        fontSize: 13.5,
                        flexShrink: 0,
                        ...(compact && { width: 'auto', flex: 1, minWidth: 0, flexShrink: 1 }),
                    }}>
                        R$ {totalDisplay}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: compact ? 0.75 : 0.5, flexShrink: 0 }}>
                        {!isService && (
                            <Tooltip title="Desconto no produto (linha)">
                                <span>
                                    <RowIconButton compact={compact} onClick={onDiscount} hoverColor="secondary.main">
                                        <Tag size={compact ? 16 : 13} />
                                    </RowIconButton>
                                </span>
                            </Tooltip>
                        )}
                        <Tooltip title="Remover item">
                            <span>
                                <RowIconButton compact={compact} onClick={onRemove} disabled={deleting} loading={deleting} hoverColor="error.main">
                                    <Trash2 size={compact ? 16 : 13} />
                                </RowIconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </>
            ) : (
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', flexShrink: 0 }}>
                    Qtd: {item.quantity} · R$ {totalDisplay}
                </Typography>
            )}
        </Box>
    );
}

export default function Operator() {
    const isMobileShell = useIsMobileShell();
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const { settings } = useEnterpriseConfigContext();
    const [quoteId, setQuoteId] = useState('');
    const [quoteNumber, setQuoteNumber] = useState('');
    const [message, setMessage] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState('success');
    const [loading, setLoading] = useState(true);
    const [loadingItems, setLoadingItems] = useState(true);
    const [loadingValues, setLoadingValues] = useState(true);
    const [itemsAutocomplete, setItemsAutocomplete] = useState([]);

    const [quoteState, setQuoteState] = useState({
        id: '',
        total: '0,00',
        totalItems: 0,
        total_services: '0,00',
        total_products: '0,00',
        total_product_item_discount: '0,00',
        cart: [],
        status: 'Pendente'
    });

    const [quoteDetails, setQuoteDetails] = useState({
        number: '',
        customer_name: '',
        customer_contact: '',
        customer_email: '',
        vehicle_name: '',
        vehicle_plate: '',
        vehicle_type: '',
        quote_date: null,
        observations: '',
        defect_reason: ''
    });

    const [uiState, setUiState] = useState({
        searchProduct: '',
        autocompleteResetKey: 0
    });

    const [itemDiscountDialog, setItemDiscountDialog] = useState({
        open: false,
        item: null,
        value: '',
        loading: false
    });

    const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
    // Mobile: troca de status numa folha de baixo (aberta pelo menu ⋮ da barra superior).
    const [statusSheetOpen, setStatusSheetOpen] = useState(false);
    const [openConvertDialog, setOpenConvertDialog] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [downloadingPDF, setDownloadingPDF] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    // Filtro Todos/Produtos/Serviços do carrinho (`.toggle-group#itemFilterGroup`)
    // — reaproveitado 1:1 de `service_order/Operator.js`, puramente client-side
    // sobre o array já carregado (não muda nenhuma chamada de API).
    const [itemFilter, setItemFilter] = useState('todos');
    // Modal "Enviar orçamento por e-mail" (`#emailModal`) — mesmo endpoint/
    // payload de antes (`POST /servicequotes/{id}/send-email`), só passou a
    // ter um passo de confirmação com o e-mail pré-preenchido e editável.
    const [openEmailModal, setOpenEmailModal] = useState(false);
    const [openWhatsappModal, setOpenWhatsappModal] = useState(false);
    // Dúvidas que o cliente enviou pelo portal público do orçamento (`/orcamento/:id`).
    const [customerMessages, setCustomerMessages] = useState([]);
    const { hasPermission } = usePermissions();
    const canSendWhatsapp = hasPermission('servicequote.send');
    const canUpdateQuote = hasPermission('servicequote.update');
    // Incluir ou Alterar libera adicionar/alterar itens (mesma regra das rotas de itens na API).
    const canEditItems = hasPermission('servicequote.create') || canUpdateQuote;
    const whatsappConnected = useWhatsappConnected(canSendWhatsapp);
    const [emailTo, setEmailTo] = useState('');
    const [openGenericItemDialog, setOpenGenericItemDialog] = useState(false);
    const [genericItemData, setGenericItemData] = useState({
        name: '',
        price: '',
        quantity: 1,
        type: 'product',
        item_id: null
    });

    const handleSearchItem = async (code) => {
        if (!code) return;
        try {
            const response = await httpClient.get(`/servicequotes/searchitem?filter=${code}`);
            setItemsAutocomplete(response.data);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar itens:', error);
        }
    };

    const addToCart = async (item, quantity = 1) => {
        const itemId = item.item_id || item.id;
        const cart = Array.isArray(quoteState.cart) ? quoteState.cart : [];
        const existingItem = cart.find(i => (i.item_id || i.id) === itemId);
        if (existingItem) {
            return await updateExistingCartItem(existingItem, item, quantity);
        } else {
            return await addNewCartItem(item, quantity);
        }
    };

    const addNewCartItem = async (item, quantity) => {
        item.quantity = quantity;

        const newItem = {
            item_id: item.item_id || item.id,
            item_name: item.item_name || item.name,
            amount_unit: item.value,
            quantity: quantity,
            total: item.value,
            amount_total: item.value,
            type: item.type,
            service_quote_id: quoteId
        };

        let response;
        if (item.type === 'product') {
            response = await httpClient.post(`/servicequotes/add/product`, newItem);
        } else {
            response = await httpClient.post(`/servicequotes/add/service`, newItem);
        }

        if (response.data.error) {
            setMessage(response.data.error);
            setSeverity('error');
            setOpenSnackbar(true);
            return;
        }

        await Promise.all([fetchItems(), fetchValues()]);
    };

    const updateExistingCartItem = async (existingItem, item, quantity) => {
        const existingItemId = existingItem.item_id || existingItem.id;
        let response;
        try {
            response = await httpClient.post(`/servicequotes/${quoteId}/item/${existingItemId}/increment`);
        } catch (error) {
            setMessage(error?.response?.data?.error || 'Erro ao adicionar item');
            setSeverity('error');
            setOpenSnackbar(true);
            return;
        }

        if (response.data.error) {
            setMessage(response.data.error);
            setSeverity('error');
            setOpenSnackbar(true);
            return;
        }

        await Promise.all([fetchItems(), fetchValues()]);
    };

    const handleDecrementItem = async (itemId) => {
        ;
        const cart = Array.isArray(quoteState.cart) ? quoteState.cart : [];
        const item = cart.find(i => (i.item_id || i.id) === itemId);
        if (!item || item.quantity <= 1) return;

        try {
            const response = await httpClient.post(`/servicequotes/${quoteId}/item/${itemId}/decrement`);
            if (response.data.error) {
                setMessage(response.data.error);
                setSeverity('error');
                setOpenSnackbar(true);
                return;
            }

            await Promise.all([fetchItems(), fetchValues()]);
        } catch (error) {
            console.error('Erro ao decrementar item:', error);
        }
    };

    const handleIncrementItem = async (itemId) => {
        ;
        const cart = Array.isArray(quoteState.cart) ? quoteState.cart : [];
        const item = cart.find(i => (i.item_id || i.id) === itemId);
        if (!item) return;

        try {
            const response = await httpClient.post(`/servicequotes/${quoteId}/item/${itemId}/increment`);
            if (response.data.error) {
                setMessage(response.data.error);
                setSeverity('error');
                setOpenSnackbar(true);
                return;
            }

            await Promise.all([fetchItems(), fetchValues()]);
        } catch (error) {
            console.error('Erro ao incrementar item:', error);
        }
    };

    const handleDeleteItem = async (itemId) => {
        const cart = Array.isArray(quoteState.cart) ? quoteState.cart : [];
        const item = cart.find(i => (i.item_id || i.id) === itemId);
        if (!item) return;

        try {
            const response = await httpClient.post(`/servicequotes/${quoteId}/item/${itemId}/remove`);
            if (response.data.error) {
                setMessage(response.data.error);
                setSeverity('error');
                setOpenSnackbar(true);
                return;
            }

            await Promise.all([fetchItems(), fetchValues()]);
        } catch (error) {
            console.error('Erro ao remover item:', error);
            setMessage('Erro ao remover item da lista');
            setSeverity('error');
            setOpenSnackbar(true);
        }
    };

    const handleGetSelected = (item) => {
        if (!item) return;
        
        // Verificar se é um item genérico
        if (item.is_generic || item.isGeneric || item.type === 'generic') {
            // Abrir modal para item genérico
            setGenericItemData({
                name: item.item_name || item.name || '',
                price: item.value || item.price || '',
                quantity: 1,
                type: item.type === 'service' ? 'service' : 'product',
                item_id: item.item_id || item.id || null
            });
            setOpenGenericItemDialog(true);
            setUiState(prev => ({
                ...prev,
                searchProduct: '',
                autocompleteResetKey: prev.autocompleteResetKey + 1
            }));
            return;
        }
        
        // Item normal - adicionar diretamente
        setUiState(prev => ({
            ...prev,
            searchProduct: '',
            autocompleteResetKey: prev.autocompleteResetKey + 1
        }));
        addToCart(item, 1);
    };

    const handleAddGenericItem = async () => {
        try {
            // Validações
            if (!genericItemData.name || genericItemData.name.trim() === '') {
                setMessage('Nome do item é obrigatório');
                setSeverity('error');
                setOpenSnackbar(true);
                return;
            }

            // Validar preço (remover pontos de milhar e converter vírgula para ponto)
            const priceValue = parseFloat(
                genericItemData.price
                    .replace(/\./g, '')
                    .replace(',', '.')
            );
            
            if (!genericItemData.price || isNaN(priceValue) || priceValue <= 0) {
                setMessage('Preço deve ser maior que zero');
                setSeverity('error');
                setOpenSnackbar(true);
                return;
            }

            if (!genericItemData.quantity || genericItemData.quantity <= 0) {
                setMessage('Quantidade deve ser maior que zero');
                setSeverity('error');
                setOpenSnackbar(true);
                return;
            }

            // Formatar preço (remover R$, espaços e pontos de milhar, substituir vírgula por ponto)
            const formattedPrice = genericItemData.price
                .replace(/R\$/g, '')
                .replace(/\s/g, '')
                .replace(/\./g, '') // Remove pontos de milhar
                .replace(',', '.'); // Substitui vírgula por ponto

            const genericPayload = {
                name: genericItemData.name.trim(),
                price: formattedPrice,
                quantity: genericItemData.quantity,
                service_quote_id: quoteId,
                type: genericItemData.type,
                item_id: genericItemData.item_id ?? '',
                item_name: genericItemData.name.trim()
            };

            const response = await httpClient.post(`/servicequotes/add/genericItem`, genericPayload);

            if (response.data.error) {
                setMessage(response.data.error);
                setSeverity('error');
                setOpenSnackbar(true);
                return;
            }

            setOpenGenericItemDialog(false);
            setGenericItemData({
                name: '',
                price: '',
                quantity: 1,
                type: 'product',
                item_id: null
            });

            await Promise.all([fetchItems(), fetchValues()]);

            setMessage('Item genérico adicionado com sucesso');
            setSeverity('success');
            setOpenSnackbar(true);
        } catch (error) {
            console.error('Erro ao adicionar item genérico:', error);
            setMessage('Erro ao adicionar item genérico: ' + (error.response?.data?.message || error.message));
            setSeverity('error');
            setOpenSnackbar(true);
        }
    };

    const handleCancelGenericItem = () => {
        setOpenGenericItemDialog(false);
        setGenericItemData({
            name: '',
            price: '',
            quantity: 1,
            type: 'product',
            item_id: null
        });
    };

    const openProductItemDiscountDialog = (item) => {
        if (!item || (item.type_item || item.type) !== 'product') return;
        setItemDiscountDialog({
            open: true,
            item,
            value: item.item_discount_type || '',
            loading: false
        });
    };

    const handleSubmitQuoteProductItemDiscount = async (removeOnly) => {
        const { item } = itemDiscountDialog;
        if (!item || !id) return;
        setItemDiscountDialog((prev) => ({ ...prev, loading: true }));
        try {
            const value = removeOnly ? '' : itemDiscountDialog.value.trim();
            if (!removeOnly && !value) {
                setMessage('Informe o desconto (ex.: 10% ou 10,00) ou use Remover.');
                setSeverity('error');
                setOpenSnackbar(true);
                setItemDiscountDialog((prev) => ({ ...prev, loading: false }));
                return;
            }
            const response = await httpClient.post(`/servicequotes/${id}/item/product/discount`, {
                product_id: item.item_id || item.id,
                is_generic: Boolean(item.is_generic),
                product_name: item.is_generic ? (item.item_name || item.name || '').trim() : '',
                value
            });
            const status = response?.status ?? 0;
            if (status < 200 || status >= 300) {
                const msg =
                    messageFromApiErrorBody(response.data) || 'Erro ao aplicar desconto no produto';
                setMessage(msg);
                setSeverity('error');
                setOpenSnackbar(true);
                setItemDiscountDialog((prev) => ({ ...prev, loading: false }));
                return;
            }
            if (response.data && typeof response.data.error === 'string' && response.data.error) {
                setMessage(response.data.error);
                setSeverity('error');
                setOpenSnackbar(true);
                setItemDiscountDialog((prev) => ({ ...prev, loading: false }));
                return;
            }
            await Promise.all([fetchItems(), fetchValues()]);
            setItemDiscountDialog({ open: false, item: null, value: '', loading: false });
        } catch (error) {
            const msg =
                messageFromApiErrorBody(error.response?.data) ||
                error.message ||
                'Erro ao aplicar desconto no produto';
            setMessage(msg);
            setSeverity('error');
            setOpenSnackbar(true);
            setItemDiscountDialog((prev) => ({ ...prev, loading: false }));
        }
    };

    const fetchItems = async () => {
        try {
            setLoadingItems(true);
            const response = await httpClient.get(`/servicequotes/${id}/items`);
            const raw = Array.isArray(response.data) ? response.data : [];
            const items = raw.map((row) => ({
                ...row,
                type: row.type_item || row.type,
                item_id: row.item_id ?? row.id,
                item_name: row.item_name || row.name || '',
                is_generic: Boolean(row.is_generic),
                item_discount_type: row.item_discount_type ?? '',
                item_discount_amount: row.item_discount_amount ?? ''
            }));
            setQuoteState(prev => ({ ...prev, cart: items }));
        } catch (error) {
            console.error('Erro ao carregar itens:', error);
            // Garantir que cart seja sempre um array mesmo em caso de erro
            setQuoteState(prev => ({ ...prev, cart: [] }));
        } finally {
            setLoadingItems(false);
        }
    };

    const fetchValues = async () => {
        try {
            setLoadingValues(true);
            const response = await httpClient.get(`/servicequotes/${id}/values`);

            setQuoteState(prev => ({
                ...prev,
                total: response.data.amount || '0,00',
                totalItems: response.data.count_items || 0,
                total_products: response.data.amount_products || '0,00',
                total_services: response.data.amount_services || '0,00',
                total_product_item_discount:
                    response.data.total_product_item_discount ??
                    prev.total_product_item_discount ??
                    '0,00'
            }));
        } catch (error) {
            console.error('Erro ao carregar valores:', error);
        } finally {
            setLoadingValues(false);
        }
    };

    const fetchQuoteDetails = async () => {
        try {
            const response = await httpClient.get(`/servicequotes/${id}`);
            if (response.status === 200) {
                setQuoteDetails({
                    customer_name: response.data?.customer_name || '',
                    customer_contact: response.data?.customer_contact || '',
                    customer_email: response.data?.customer_email || '',
                    vehicle_name: response.data?.vehicle_name || '',
                    vehicle_plate: response.data?.vehicle_plate || '',
                    vehicle_type: response.data?.vehicle_type || '',
                    quote_date: response.data?.quote_date || null,
                    sent_at: response.data?.sent_at || null,
                    approved_at: response.data?.approved_at || null,
                    converted_at: response.data?.converted_at || null,
                    observations: response.data?.observations || '',
                    defect_reason: response.data?.defect_reason || ''
                });
                setQuoteState(prev => ({
                    ...prev,
                    status: response.data?.status || 'Pendente'
                }));
                if (response.data?.number) {
                    setQuoteNumber(response.data.number);
                }
            }
        } catch (error) {
            console.error('Erro ao carregar detalhes do orçamento:', error);
        }
    };

    const handleStatusMenuOpen = (event) => {
        setStatusMenuAnchor(event.currentTarget);
    };

    const handleStatusMenuClose = () => {
        setStatusMenuAnchor(null);
    };

    const handleStatusChange = async (newStatus) => {
        try {
            // Atualizar o estado imediatamente para feedback visual
            setQuoteState(prev => ({
                ...prev,
                status: newStatus
            }));

            const response = await httpClient.post(`/servicequotes/${id}/status`, {
                status: newStatus
            });

            if (response.status === 200) {
                // Atualizar com o status retornado pela API (caso seja diferente)
                const updatedStatus = response.data?.status || newStatus;
                setQuoteState(prev => ({
                    ...prev,
                    status: updatedStatus
                }));
                setMessage('Status atualizado com sucesso');
                setSeverity('success');
                setOpenSnackbar(true);
            }
        } catch (error) {
            console.error('Erro ao atualizar status:', error);
            // Reverter o status em caso de erro
            await fetchQuoteDetails();
            setMessage('Erro ao atualizar status do orçamento');
            setSeverity('error');
            setOpenSnackbar(true);
        } finally {
            handleStatusMenuClose();
        }
    };

    const handleConvertToOS = () => {
        setOpenConvertDialog(true);
    };

    const handleEditQuote = () => {
        setOpenEditModal(true);
    };

    const handleEditSuccess = async () => {
        setMessage('Orçamento atualizado com sucesso');
        setSeverity('success');
        setOpenSnackbar(true);
        // Recarregar os dados do orçamento
        await fetchQuoteDetails();
    };

    const handleDeleteQuote = () => {
        setOpenDeleteDialog(true);
    };

    const confirmDeleteQuote = async () => {
        try {
            const response = await httpClient.delete(`/servicequotes/${id}`);
            if (response.status === 200 || response.status === 204) {
                setMessage('Orçamento removido com sucesso');
                setSeverity('success');
                setOpenSnackbar(true);
                setOpenDeleteDialog(false);
                // Redirecionar para a lista após um pequeno delay para mostrar a mensagem
                setTimeout(() => {
                    navigate('/servicequotes');
                }, 500);
            }
        } catch (error) {
            console.error('Erro ao remover orçamento:', error);
            setMessage('Erro ao remover orçamento: ' + (error.response?.data?.message || error.message));
            setSeverity('error');
            setOpenSnackbar(true);
            setOpenDeleteDialog(false);
        }
    };

    const confirmConvertToOS = async () => {
        try {
            const response = await httpClient.post(`/servicequotes/${id}/convert`);
            if (response.status === 200 || response.status === 201) {
                setMessage('Ordem de Serviço gerada com sucesso');
                setSeverity('success');
                setOpenSnackbar(true);
                setOpenConvertDialog(false);
                // Redirecionar para a OS gerada; sem id na resposta, atualiza o status do orçamento
                if (response.data?.id) {
                    navigate(`/serviceorders/${response.data.id}/view`);
                } else {
                    await fetchQuoteDetails();
                }
            }
        } catch (error) {
            console.error('Erro ao converter orçamento:', error);
            setMessage('Erro ao gerar ordem a partir do orçamento: ' + (error.response?.data?.message || error.message));
            setSeverity('error');
            setOpenSnackbar(true);
        }
    };

    const getTemplateForQuote = async () => {
        const response = await httpClient.get(`/servicequotes/${id}/pdf`, {
            responseType: 'blob'
        });
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(response.data);
        });
    };

    const fetchCustomerMessages = async () => {
        try {
            const response = await httpClient.get(`/servicequotes/${id}/messages`);
            if (response.status === 200 && Array.isArray(response.data)) {
                setCustomerMessages(response.data);
            }
        } catch (error) {
            console.error('Erro ao buscar dúvidas do cliente:', error);
        }
    };

    // Link público (portal do cliente): o token é gerado pela API (HMAC por
    // orçamento), diferente do link da OS que deriva da empresa no front.
    const handleCopyPortalLink = async () => {
        try {
            const response = await httpClient.get(`/servicequotes/${id}/share-token`);
            const token = response.data?.token;
            if (response.status !== 200 || !token) {
                throw new Error('Token indisponível');
            }
            const link = `${window.location.origin}/orcamento/${id}?t=${encodeURIComponent(token)}`;
            await navigator.clipboard.writeText(link);
            setMessage('Link do orçamento copiado');
            setSeverity('success');
        } catch (error) {
            console.error('Erro ao copiar link do orçamento:', error);
            setMessage('Erro ao gerar o link do orçamento');
            setSeverity('error');
        }
        setOpenSnackbar(true);
    };

    const handleDownloadPDF = async () => {
        try {
            setDownloadingPDF(true);
            const response = await httpClient.get(`/servicequotes/${id}/pdf`, {
                responseType: 'blob'
            });
            
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const urlBlob = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = urlBlob;
            link.download = `orcamento_servico_${quoteNumber || id}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(urlBlob);
            
            setMessage('PDF baixado com sucesso');
            setSeverity('success');
            setOpenSnackbar(true);
        } catch (error) {
            console.error('Erro ao baixar PDF:', error);
            setMessage('Erro ao baixar PDF do orçamento');
            setSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setDownloadingPDF(false);
        }
    };

    // Abre o modal de envio (`#emailModal`) com o e-mail do cliente
    // pré-preenchido — o próprio envio (endpoint/payload) só acontece em
    // `confirmSendEmail`, disparado pelo botão "Enviar" do modal.
    const handleOpenEmailModal = () => {
        setEmailTo(quoteDetails.customer_email || '');
        setOpenEmailModal(true);
    };

    const confirmSendEmail = async () => {
        const targetEmail = (emailTo || '').trim();
        if (!targetEmail) {
            setMessage('Informe o e-mail do destinatário');
            setSeverity('warning');
            setOpenSnackbar(true);
            return;
        }

        try {
            setSendingEmail(true);
            const response = await httpClient.post(`/servicequotes/${id}/send-email`, {
                email: targetEmail,
                template: await getTemplateForQuote()
            });

            if (response.status === 200 || response.status === 201) {
                setMessage('Orçamento enviado para ' + targetEmail);
                setSeverity('success');
                setOpenSnackbar(true);
                setOpenEmailModal(false);
                fetchQuoteDetails();
            }
        } catch (error) {
            console.error('Erro ao enviar email:', error);
            setMessage('Erro ao enviar orçamento por email: ' + (error.response?.data?.message || error.message));
            setSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setSendingEmail(false);
        }
    };

    const generateServiceOrderPDF = async (quoteId, download = false, returnHtml = false) => {
        if (!quoteId) return;

        const storage = localStorage;

        const response = await httpClient.get(`/servicequotes/${quoteId}`);
        const data = response.data;

        ;

        if (!data) return;

        const responseEnterprise = await httpClient.get(`/enterprise/account-info`);
        const enterpriseData = responseEnterprise.data;

        ;

        const showInstagram = !!enterpriseData.instagram;
        const showFacebook = !!enterpriseData.facebook;
        const showTerms = !!enterpriseData.order_terms;
        const showVehicle = (storage.getItem('vehicle') === 'true') && (data.vehicle_name !== null && data.vehicle_name !== '-');
        const hasLogo = !!enterpriseData.logo;

        // Determinar o formato do logo e criar imgSrc apropriado
        let imgSrc = '';
        if (hasLogo) {
            const logo = enterpriseData.logo;
            // Se já é uma URL completa (http/https), tentar converter para base64 para evitar problemas de CORS
            if (logo.startsWith('http://') || logo.startsWith('https://')) {
                try {
                    // Tentar carregar a imagem e converter para base64
                    const response = await fetch(logo);
                    const blob = await response.blob();
                    const reader = new FileReader();
                    imgSrc = await new Promise((resolve, reject) => {
                        reader.onloadend = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    });
                } catch (error) {
                    console.warn('Erro ao converter logo URL para base64, usando URL diretamente:', error);
                    // Se falhar, usar a URL diretamente
                    imgSrc = logo;
                }
            } else if (logo.startsWith('data:')) {
                // Já é um data URI, usar diretamente
                imgSrc = logo;
            } else {
                // Assumir que é base64 e adicionar prefixo
                imgSrc = `data:image/jpg;base64,${logo}`;
            }
        }

        const responseItems = await httpClient.get(`/servicequotes/${quoteId}/items`);
        const items = responseItems.data;

        const responseValues = await httpClient.get(`/servicequotes/${quoteId}/values`);
        const values = responseValues.data;

        const htmlItems = items && items.map(item => `
          <div class="item-row">
            <div class="item-quantity">${item.quantity}</div>
            <div class="item-description">${item.item_name}</div>
            <div class="item-price">${item.amount_unit}</div>
            <div class="item-total">${item.amount_total}</div>
          </div>
        `).join('');

        const htmlString = `
          <style>
            * {
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              line-height: 1.4;
              color: #333;
              background: #fff;
              margin: 0;
              padding: 0;
            }
            
            .header-section {
              background: #f8f9fa;
              
              padding: 15px;
              margin-bottom: 15px;
              border-radius: 8px;
            }
            
            .enterprise-info {
              display: flex;
              align-items: center;
              gap: 15px;
            }
            
            .enterprise-logo {
              width: 60px;
              height: 60px;
              border-radius: 6px;
              object-fit: cover;
              flex-shrink: 0;
            }
            
            .enterprise-logo-fallback {
              width: 60px;
              height: 60px;
              background: #667eea;
              border-radius: 6px;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: bold;
              font-size: 20px;
              flex-shrink: 0;
            }
            
            .enterprise-details {
              flex: 1;
            }
            
            .enterprise-name {
              font-size: 20px;
              font-weight: 700;
              margin-bottom: 4px;
              color: #667eea;
            }
            
            .enterprise-banner {
              font-size: 14px;
              color: #666;
              margin-bottom: 3px;
            }
            
            .enterprise-document {
              font-size: 12px;
              color: #666;
            }
            
            .order-number {
              background: #667eea;
              color: white;
              padding: 10px 15px;
              border-radius: 6px;
              text-align: center;
              margin-bottom: 8px;
            }
            
            .order-number h2 {
              font-size: 18px;
              font-weight: 700;
              margin: 0;
            }
            
            .order-number .order-num {
              font-size: 14px;
              margin-top: 2px;
            }
            
            .contact-info {
              display: flex;
              flex-direction: column;
              gap: 3px;
              margin-top: 12px;
              text-align: right;
            }
            
            .contact-item {
              display: flex;
              align-items: center;
              justify-content: flex-end;
              gap: 5px;
              font-size: 12px;
              color: #666;
            }
            
            .phone-row {
              display: flex;
              gap: 10px;
              justify-content: flex-end;
              margin-bottom: 3px;
              flex-wrap: nowrap;
            }
            
            .phone-item {
              display: flex;
              align-items: center;
              gap: 2px;
              font-size: 12px;
              color: #666;
              white-space: nowrap;
            }
            
            .contact-item img {
              width: 12px;
              height: 12px;
            }
            
            .social-links {
              display: flex;
              gap: 15px;
              margin-top: 5px;
              justify-content: flex-end;
              flex-wrap: nowrap;
            }
            
            .social-item {
              display: flex;
              align-items: center;
              gap: 4px;
              font-size: 11px;
              color: #666;
              white-space: nowrap;
            }
            
            .social-item img {
              width: 14px;
              height: 14px;
            }
            
            .section-divider {
              height: 1px;
              background: #667eea;
              border: none;
              margin: 15px 0;
            }
            
            .info-section {
              background: #f8f9fa;
              padding: 12px;
              border-radius: 6px;
              margin-bottom: 12px;
              border-left: 3px solid #667eea;
            }
            
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 10px;
            }
            
            .info-item {
              display: flex;
              flex-direction: column;
              gap: 2px;
            }
            
            .info-label {
              font-weight: 600;
              color: #667eea;
              font-size: 10px;
              text-transform: uppercase;
              letter-spacing: 0.3px;
            }
            
            .info-value {
              font-size: 12px;
              color: #333;
            }
            
            .vehicle-section {
              background: #e8f4fd;
              padding: 12px;
              border-radius: 6px;
              margin-bottom: 12px;
              border-left: 3px solid #2196f3;
            }
            
            .vehicle-grid {
              display: grid;
              grid-template-columns: 2fr 1fr 2fr 1fr;
              gap: 8px;
            }
            
            .items-section {
              background: white;
              border: 1px solid #e0e0e0;
              border-radius: 6px;
              overflow: hidden;
              margin-bottom: 15px;
            }
            
            .items-header {
              background: #667eea;
              color: white;
              padding: 8px 12px;
              font-weight: 600;
              display: grid;
              grid-template-columns: 50px 1fr 80px 70px;
              gap: 8px;
              align-items: center;
              font-size: 12px;
            }
            
            .items-body {
              padding: 0;
            }
            
            .item-row {
              display: grid;
              grid-template-columns: 50px 1fr 80px 70px;
              gap: 8px;
              align-items: center;
              padding: 8px 12px;
              border-bottom: 1px solid #f0f0f0;
              font-size: 11px;
            }
            
            .item-row:last-child {
              border-bottom: none;
            }
            
            .item-quantity {
              font-weight: 600;
              color: #667eea;
              text-align: center;
            }
            
            .item-description {
              color: #333;
              font-weight: 500;
            }
            
            .item-price {
              text-align: right;
              font-weight: 600;
              color: #333;
            }
            
            .item-total {
              text-align: right;
              font-weight: 700;
              color: #2e7d32;
            }
            
            .totals-section {
              background: #f8f9fa;
              padding: 12px;
              border-radius: 6px;
              margin-bottom: 15px;
            }
            
            .totals-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 15px;
            }
            
            .terms-section {
              background: #fff3e0;
              padding: 10px;
              border-radius: 6px;
              border-left: 3px solid #ff9800;
            }
            
            .terms-title {
              font-weight: 700;
              color: #e65100;
              margin-bottom: 8px;
              font-size: 12px;
            }
            
            .terms-content {
              font-size: 10px;
              line-height: 1.4;
              color: #333;
              text-align: justify;
            }
            
            .totals-right {
              display: flex;
              flex-direction: column;
              gap: 6px;
            }
            
            .total-item {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 4px 0;
              font-size: 12px;
            }
            
            .total-label {
              font-weight: 600;
              color: #666;
            }
            
            .total-value {
              font-weight: 700;
              color: #333;
            }
            
            .total-final {
              border-top: 2px solid #667eea;
              padding-top: 6px;
              margin-top: 4px;
            }
            
            .total-final .total-label {
              font-size: 14px;
              color: #667eea;
            }
            
            .total-final .total-value {
              font-size: 16px;
              color: #667eea;
            }
            
            .signature-section {
              text-align: center;
              margin-top: 20px;
              padding: 15px;
            }
            
            .signature-line {
              border-bottom: 2px solid #333;
              width: 250px;
              margin: 0 auto 8px;
              height: 30px;
            }
            
            .signature-name {
              font-weight: 600;
              color: #333;
              font-size: 14px;
            }
            
            /* Responsive Design */
            @media (max-width: 768px) {
              .header-section {
                padding: 12px;
              }
              
              .enterprise-info {
                gap: 10px;
              }
              
              .enterprise-logo {
                width: 50px;
                height: 50px;
              }
              
              .enterprise-logo-fallback {
                width: 50px;
                height: 50px;
                font-size: 18px;
              }
              
              .enterprise-name {
                font-size: 18px;
              }
              
              .order-number h2 {
                font-size: 16px;
              }
              
              .info-grid {
                grid-template-columns: 1fr 1fr;
                gap: 8px;
              }
              
              .vehicle-grid {
                grid-template-columns: 1fr 1fr;
                gap: 6px;
              }
              
              .items-header,
              .item-row {
                grid-template-columns: 40px 1fr 70px 60px;
                gap: 6px;
                padding: 6px 8px;
                font-size: 10px;
              }
              
              .totals-grid {
                grid-template-columns: 1fr;
                gap: 10px;
              }
            }
            
            @media (max-width: 480px) {
              .header-section {
                padding: 10px;
              }
              
              .enterprise-info {
                gap: 8px;
              }
              
              .enterprise-logo {
                width: 40px;
                height: 40px;
              }
              
              .enterprise-logo-fallback {
                width: 40px;
                height: 40px;
                font-size: 16px;
              }
              
              .enterprise-name {
                font-size: 16px;
              }
              
              .order-number h2 {
                font-size: 14px;
              }
              
              .info-grid {
                grid-template-columns: 1fr;
                gap: 6px;
              }
              
              .items-header,
              .item-row {
                grid-template-columns: 35px 1fr 60px 50px;
                gap: 4px;
                padding: 4px 6px;
                font-size: 9px;
              }
              
              .signature-line {
                width: 180px;
              }
            }
            
            @media print {
              body { 
                -webkit-print-color-adjust: exact; 
                print-color-adjust: exact;
                font-size: 11px;
                line-height: 1.3;
              }
              
              .header-section { 
                break-inside: avoid; 
                margin-bottom: 10px;
                padding: 10px;
              }
              
              .info-section,
              .vehicle-section {
                break-inside: avoid;
                margin-bottom: 8px;
                padding: 8px;
              }
              
              .items-section { 
                break-inside: avoid; 
                margin-bottom: 10px;
              }
              
              .items-header {
                padding: 6px 8px;
                font-size: 10px;
              }
              
              .item-row {
                padding: 4px 8px;
                font-size: 9px;
              }
              
              .totals-section {
                break-inside: avoid;
                margin-bottom: 10px;
                padding: 8px;
              }
              
              .signature-section {
                margin-top: 15px;
                padding: 10px;
              }
              
              .signature-line {
                width: 200px;
                height: 25px;
              }
              
              .signature-name {
                font-size: 12px;
              }
            }
          </style>
          <html>
            <head>
              <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@4.3.1/dist/css/bootstrap.css">
              <title>ORÇAMENTO DE SERVIÇO</title>
            </head>
            <body>
              <div class="container-fluid">
                <!-- Header Section -->
                <div class="header-section">
                  <div class="row align-items-center">
                    <div class="col-md-8">
                      <div class="enterprise-info">
                        ${hasLogo ? 
                          `<img class="enterprise-logo" src="${imgSrc}" alt="Logo da empresa" style="width:60px; height:60px; border-radius:6px; object-fit:cover; flex-shrink:0;">
                           <div class="enterprise-logo-fallback" style="display:none; width:60px; height:60px; background:#667eea; border-radius:6px; align-items:center; justify-content:center; color:white; font-weight:bold; font-size:20px;">${enterpriseData.legal_name ? enterpriseData.legal_name.charAt(0).toUpperCase() : 'L'}</div>` :
                          `<div class="enterprise-logo-fallback" style="width:60px; height:60px; background:#667eea; border-radius:6px; display:flex; align-items:center; justify-content:center; color:white; font-weight:bold; font-size:20px;">${enterpriseData.legal_name ? enterpriseData.legal_name.charAt(0).toUpperCase() : 'L'}</div>`
                        }
                        <div class="enterprise-details">
                          <div class="enterprise-name">${enterpriseData.legal_name}</div>                         
                          <div class="enterprise-document">${enterpriseData.cnpj}</div>
                         
                        </div>
                      </div>
                        </div>
                        <div class="col-md-4">
                      <div class="order-number">
                        <h2>ORÇAMENTO</h2>
                        <div class="order-num">N.º ${data.number}</div>
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-8">
                    </div>
                    <div class="col-md-4">
                      <div class="contact-info">
                       <div style="margin-top: 8px; font-size: 12px; color: #666;">
                            <span style="margin-right:4px;vertical-align:middle; font-size:14px;">📍</span>
                            <span>${enterpriseData.address}, ${enterpriseData.number_address}, ${enterpriseData.neighborhood}, ${enterpriseData.city}/${enterpriseData.state}</span>
                          </div>
                        <div class="phone-row">
                          <div class="phone-item">
                            <span style="margin-right:4px;vertical-align:middle; font-size:14px;">📱</span>
                            <span>${formatPhone(enterpriseData.cell_phone)}</span>
                          </div>
                          <div class="phone-item">
                            <span style="margin-right:4px;vertical-align:middle; font-size:14px;">📞</span>
                            <span>${formatPhone(enterpriseData.phone_number)}</span>
                          </div>
                        </div>
                        <div class="social-links">
                          ${showFacebook ? `
                            <div class="social-item">
                              <span style="margin-right:4px;vertical-align:middle; font-size:14px;">📘</span>
                              <span>${enterpriseData.facebook}</span>
                            </div>
                          ` : ''}
                          ${showInstagram ? `
                            <div class="social-item">
                              <span style="margin-right:4px;vertical-align:middle; font-size:14px;">📷</span>
                              <span>${enterpriseData.instagram}</span>
                            </div>
                          ` : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <hr class="section-divider">
                
                <!-- Customer Information Section -->
                <div class="info-section">
                  <div class="info-grid">
                    <div class="info-item">
                      <div class="info-label">Data</div>
                      <div class="info-value">${formatDate(data.quote_date)}</div>
                    </div>
                    <div class="info-item">
                      <div class="info-label">Cliente</div>
                      <div class="info-value">${data.customer_name}</div>
                    </div>
                   
                   
                    <div class="info-item">
                      <div class="info-label">Contato</div>
                      <div class="info-value">${formatPhone(data.customer_contact)}</div>
                    </div>
                  </div>
                </div>
                
                ${showVehicle ? `
                  <!-- Vehicle Information Section -->
                  <div class="vehicle-section">
                    <div class="vehicle-grid">
                      <div class="info-item">
                        <div class="info-label">Veículo</div>
                        <div class="info-value">${data.vehicle_name} ${data.vehicle_model}</div>
                      </div>
                      <div class="info-item">
                        <div class="info-label">Placa</div>
                        <div class="info-value">${data.vehicle_plate || '-'}</div>
                    </div>
                      <div class="info-item">
                        <div class="info-label">Chassi</div>
                        <div class="info-value">${data.vehicle_chassi || 'Não informado'}</div>
                    </div>
                      <div class="info-item">
                        <div class="info-label">Quilometragem</div>
                        <div class="info-value">${data.vehicle_kilometer} km</div>
                    </div>
                    </div>
                </div>
                ` : ''}
                
                <!-- Items Section -->
                <div class="items-section">
                  <div class="items-header">
                    <div>QTD</div>
                    <div>DESCRIÇÃO</div>
                    <div>VALOR UNID.</div>
                    <div>TOTAL R$</div>
                    </div>
                  <div class="items-body">
                    ${htmlItems || ''}
                  </div>
                </div>
                <!-- Totals and Terms Section -->
                <div class="totals-section">
                  <div class="totals-grid">
                    ${showTerms ? `
                      <div class="terms-section">
                        <div class="terms-title">LEIA COM ATENÇÃO OS TERMOS E CONDIÇÕES:</div>
                        <div class="terms-content">
                        ${data.order_terms || ''}
                      </div>
                    </div>
                    ` : '<div></div>'}
                    
                    <div class="totals-right">
                      <div class="total-item">
                        <span class="total-label">Total Serviços:</span>
                        <span class="total-value">R$ ${values.amount_services}</span>
                      </div>
                      <div class="total-item">
                        <span class="total-label">Total Peças:</span>
                        <span class="total-value">R$ ${values.amount_products}</span>
                      </div>
                      <div class="total-item total-final">
                        <span class="total-label">TOTAL GERAL:</span>
                        <span class="total-value">R$ ${values.amount}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Signature Section -->
                <div class="signature-section">
                  <div class="signature-line"></div>
                  <div class="signature-name">${data.customer_name}</div>
                  <div style="font-size: 14px; color: #666; margin-top: 5px;">Cliente</div>
                </div>
              </div>
            </body>
          </html>
        `;

        const options = {
            filename: `orcamento_servico_${data.number}.pdf`,
            image: { type: 'png', quality: 1 },
            html2canvas: { 
                scale: 2,
                useCORS: true,
                allowTaint: false,
                logging: false
            },
            jsPDF: { unit: 'mm', format: 'a4' }
        };

        if (returnHtml) {
            return html2pdf()
                .set(options)
                .from(htmlString)
                .toPdf()
                .get('pdf')
                .then(function (pdf) {
                    const pdfBlob = pdf.output('blob');
                    return new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            const base64data = reader.result.split(',')[1];
                            resolve(base64data);
                        };
                        reader.readAsDataURL(pdfBlob);
                    });
                });
        } else if (download) {
            html2pdf()
                .set(options)
                .from(htmlString)
                .toPdf()
                .get('pdf')
                .save();
        } else {
            html2pdf()
                .set(options)
                .from(htmlString)
                .toPdf()
                .get('pdf')
                .then(function (pdf) {
                    const pdfUrl = URL.createObjectURL(pdf.output('blob'));
                    window.open(pdfUrl);
                });
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        const day = String(date.getUTCDate()).padStart(2, '0');
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const year = date.getUTCFullYear();
        const hours = String(date.getUTCHours()).padStart(2, '0');
        const minutes = String(date.getUTCMinutes()).padStart(2, '0');

        return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

    useEffect(() => {
        if (id) {
            setQuoteId(id);
            setLoading(true);
            Promise.all([fetchItems(), fetchValues(), fetchQuoteDetails(), fetchCustomerMessages()]).finally(() => {
                setLoading(false);
            });
        }
    }, [id]);
    // "Válido até" — mesmo campo de UX só-visual (+7 dias sobre a data do
    // orçamento) já usado em `service_quotes/Generate.js`; a API não tem
    // coluna equivalente hoje (`QuoteInput` só tem `quote_date`), então não é
    // persistido — mesma ressalva documentada lá.
    const validUntilDisplay = (() => {
        if (!quoteDetails.quote_date) return null;
        const base = new Date(quoteDetails.quote_date);
        if (isNaN(base.getTime())) return null;
        base.setUTCDate(base.getUTCDate() + 7);
        const day = String(base.getUTCDate()).padStart(2, '0');
        const month = String(base.getUTCMonth() + 1).padStart(2, '0');
        const year = base.getUTCFullYear();
        return `${day}/${month}/${year}`;
    })();

    const breadcrumbs = [
        { title: 'Orçamentos de Serviço', link: '/servicequotes' },
    ];

    const statusKey = statusKeyOf(quoteState.status);
    const converted = statusKey === 'ordem gerada';

    // Etapas do status em linha (header desktop, igual à OS). O estado vem do status atual;
    // a data vem de quote_date/sent_at/approved_at/converted_at (orçamentos antigos não têm
    // as três últimas e ficam sem data).
    const stepRank = STATUS_ORDER.indexOf(statusKey);
    // Instantes reais (timestamptz) em horário local, igual ao stepStamp da OS.
    const stepStamp = (value) => {
        const m = value ? dayjs(value) : null;
        return m && m.isValid() && m.year() >= 1900 ? `${m.format('DD/MM')} · ${m.format('HH:mm')}` : undefined;
    };
    const stepCreated = orderMoment(quoteDetails.quote_date);
    const statusSteps = [
        { title: 'Orçamento criado', sub: stepCreated ? `${stepCreated.format('DD/MM')} · ${stepCreated.format('HH:mm')}` : undefined, state: 'done' },
        {
            title: 'Enviado',
            sub: stepRank >= 1 ? (statusKey === 'aguardando aprovacao' ? 'Aguardando aprovação' : stepStamp(quoteDetails.sent_at)) : '—',
            state: stepRank >= 1 ? 'done' : 'off',
        },
        { title: 'Aprovado', sub: stepRank >= 3 ? stepStamp(quoteDetails.approved_at) : '—', state: stepRank >= 3 ? 'done' : 'off' },
        { title: 'Ordem gerada', sub: converted ? stepStamp(quoteDetails.converted_at) : '—', state: converted ? 'done' : 'off' },
    ];

    // Ações do cabeçalho computadas por status (item (3) do escopo desta
    // tela, DESIGN-SYSTEM.md §6.3): Novo/Enviado/Aguardando Aprovação ->
    // Editar/Enviar por e-mail/Excluir; Aprovado -> + Converter em OS;
    // Convertido -> só Baixar PDF.
    const headerActions = [];
    if (!converted) {
        if (canUpdateQuote) headerActions.push({ key: 'edit', label: 'Editar', icon: Pencil, onClick: handleEditQuote });
        headerActions.push({ key: 'copy-link', label: 'Copiar link', icon: Link2, onClick: handleCopyPortalLink });
        if (canSendWhatsapp) headerActions.push({ key: 'mail', label: 'Enviar por e-mail', icon: Mail, onClick: handleOpenEmailModal });
        if (canSendWhatsapp) headerActions.push({
            key: 'whatsapp-send',
            label: 'Enviar por WhatsApp',
            icon: Send,
            onClick: () => setOpenWhatsappModal(true),
            disabled: !whatsappConnected,
            tooltip: whatsappConnected ? '' : 'WhatsApp não conectado. Conecte o número em Configurações > WhatsApp.',
        });
        if (statusKey === 'aprovado' && canUpdateQuote) {
            headerActions.push({ key: 'convert', label: 'Gerar Ordem', icon: ArrowLeftRight, solid: true, onClick: handleConvertToOS });
        }
        if (hasPermission('servicequote.delete')) headerActions.push({ key: 'delete', label: 'Excluir', icon: Trash2, danger: true, onClick: handleDeleteQuote });
    } else {
        headerActions.push({ key: 'pdf', label: 'Baixar PDF', icon: Download, onClick: handleDownloadPDF, loading: downloadingPDF, disabled: downloadingPDF });
    }

    // Mobile: as ações do cabeçalho vão para a folha "Ações" (⋯ do AppBar, mesmo
    // padrão da OS), com as mesmas condições de permissão/status; o desktop
    // segue com os botões do hero. Destrutivas ficam por último.
    const mobileActions = [
        ...(!converted && canUpdateQuote ? [{ key: 'status', label: 'Alterar status', onClick: () => setStatusSheetOpen(true) }] : []),
        ...headerActions.filter((a) => !a.danger),
        ...headerActions.filter((a) => a.danger),
    ].map(({ key, label, onClick, disabled, danger, tooltip }) => ({
        key,
        label,
        onClick,
        disabled: !!disabled,
        destructive: !!danger,
        sub: tooltip || undefined,
    }));

    const cartArray = Array.isArray(quoteState.cart) ? quoteState.cart : [];
    const filteredCart = cartArray.filter(
        (item) => itemFilter === 'todos' || (item.type_item || item.type) === itemFilter
    );
    const hasProductItemDiscount = quoteState.total_product_item_discount && quoteState.total_product_item_discount !== '0,00';

    return (
        <PageScaffold
            title="Orçamento de Serviço"
            links={breadcrumbs}
            openSnackbar={openSnackbar}
            message={message}
            setOpenSnackbar={setOpenSnackbar}
            severity={severity}
        >
            {isMobileShell ? (
                <MobileQuoteOperator
                    loading={loading}
                    quoteNumber={quoteNumber}
                    status={quoteState.status}
                    details={quoteDetails}
                    usesVehicle={Boolean(settings?.os_use_vehicle)}
                    quoteTotal={quoteState.total}
                    productDiscount={hasProductItemDiscount ? quoteState.total_product_item_discount : ''}
                    items={cartArray}
                    loadingItems={loadingItems}
                    editable={!converted && canEditItems}
                    converted={converted}
                    canChangeStatus={canUpdateQuote && !converted}
                    actions={mobileActions}
                    statusSheetOpen={statusSheetOpen}
                    onStatusSheetClose={() => setStatusSheetOpen(false)}
                    onChangeStatus={(s) => {
                        if (s === 'ordem gerada') {
                            handleConvertToOS();
                            return;
                        }
                        handleStatusChange(s);
                    }}
                    customerMessages={customerMessages}
                    onConclude={() => navigate(`/servicequotes/${id}/view`, { replace: true })}
                    onStep={(lineId, delta) => (delta > 0 ? handleIncrementItem(lineId) : handleDecrementItem(lineId))}
                    onDiscount={openProductItemDiscountDialog}
                    onRemove={handleDeleteItem}
                    onSearch={handleSearchItem}
                    searchResults={itemsAutocomplete}
                    onAddItem={handleGetSelected}
                />
            ) : (
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* CABEÇALHO — os-header (gradiente de marca, §2.4/§6.3),
                    reaproveitado 1:1 de `service_order/Operator.js`. */}
                {loading ? (
                    <Card elevation={0} sx={{ borderRadius: '20px', mb: 2.25, p: '22px 24px', background: (t) => t.palette.gradient.brand, boxShadow: (t) => t.customShadows.md }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.25, flexWrap: 'wrap' }}>
                            <Skeleton variant="circular" width={36} height={36} sx={{ bgcolor: 'rgba(255,255,255,.25)' }} />
                            <Skeleton variant="text" width={200} height={34} sx={{ bgcolor: 'rgba(255,255,255,.25)' }} />
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' }, gap: 2.25 }}>
                            {[...Array(5)].map((_, i) => (
                                <Skeleton key={i} variant="text" width={90} height={36} sx={{ bgcolor: 'rgba(255,255,255,.2)' }} />
                            ))}
                        </Box>
                    </Card>
                ) : (
                    <Card elevation={0} sx={{ borderRadius: '20px', mb: 2.25, p: '22px 24px', background: (t) => t.palette.gradient.brand, boxShadow: (t) => t.customShadows.md }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                {!isMobileShell && <Tooltip title="Voltar">
                                    <IconButton
                                        // Veio da lista: volta por histórico (POP) para a lista restaurar os filtros.
                                        onClick={() => (location.state?.fromList ? navigate(-1) : navigate('/servicequotes'))}
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: '10px',
                                            bgcolor: 'rgba(255,255,255,.15)',
                                            border: '1px solid rgba(255,255,255,.25)',
                                            color: '#fff',
                                            '&:hover': { bgcolor: 'rgba(255,255,255,.25)' },
                                        }}
                                    >
                                        <ArrowLeft size={16} strokeWidth={2} />
                                    </IconButton>
                                </Tooltip>}
                                <Typography component="h1" sx={{ fontSize: '21px', fontWeight: 800, letterSpacing: '-.01em', color: '#fff' }}>
                                    ORÇAMENTO Nº {quoteNumber || id}
                                </Typography>
                                {/* Status vira um botão com menu dropdown (`status-menu`) — trava
                                    (não abre o menu) quando já Convertido. */}
                                <StatusMenuTrigger statusKey={statusKey} disabled={converted || !canUpdateQuote} onClick={handleStatusMenuOpen} />
                            </Box>

                            {!isMobileShell && (
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {headerActions.map(({ key, ...action }) => (
                                        <HeaderActionButton key={key} {...action} />
                                    ))}
                                </Box>
                            )}
                        </Box>

                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
                            gap: 2.25,
                        }}>
                            <MetaItem label="Data do orçamento" value={formatDate(quoteDetails.quote_date)} />
                            <MetaItem label="Válido até" value={validUntilDisplay} />
                            <MetaItem
                                label="Cliente"
                                value={quoteDetails.customer_name}
                                sub={quoteDetails.customer_contact ? formatPhone(quoteDetails.customer_contact) : null}
                            />
                            {settings?.os_use_vehicle && (
                                <MetaItem
                                    label="Veículo"
                                    value={quoteDetails.vehicle_name}
                                    sub={
                                        quoteDetails.vehicle_plate && quoteDetails.vehicle_type !== 'bicycle'
                                            ? `Placa ${quoteDetails.vehicle_plate}`
                                            : null
                                    }
                                />
                            )}
                            <MetaItem label="Motivo/Defeito" value={quoteDetails.defect_reason} />
                            <MetaItem label="Observações" value={quoteDetails.observations} />
                        </Box>

                        {/* ETAPAS DO STATUS (status + data), em linha, igual à OS */}
                        <Box sx={{ mt: 3, p: '14px 18px', borderRadius: '14px', bgcolor: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.18)' }}>
                            <HeaderStatusSteps steps={statusSteps} />
                        </Box>

                        {converted && (
                            <Box sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                fontSize: '11.5px',
                                fontWeight: 700,
                                padding: '5px 11px',
                                borderRadius: '20px',
                                bgcolor: 'rgba(255,255,255,.15)',
                                color: '#fff',
                                mt: 1.75,
                            }}>
                                <ArrowLeftRight size={12} strokeWidth={2} />
                                Ordem de Serviço gerada
                            </Box>
                        )}
                    </Card>
                )}

                {/* MENU: troca de status (`#statusMenu`) — escolher "Convertido"
                    abre o mesmo modal de "Converter em OS" em vez de aplicar
                    direto, pra manter uma via única de conversão. */}
                <Menu
                    anchorEl={statusMenuAnchor}
                    open={Boolean(statusMenuAnchor)}
                    onClose={handleStatusMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <Typography sx={{ fontSize: 10.5, color: 'text.disabled', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', px: 1.75, pt: 0.5, pb: 0.75 }}>
                        Alterar status do orçamento
                    </Typography>
                    {STATUS_ORDER.map((s) => {
                        const meta = STATUS_META[s];
                        const isCurrent = s === statusKey;
                        return (
                            <MenuItem
                                key={s}
                                selected={isCurrent}
                                onClick={() => {
                                    handleStatusMenuClose();
                                    if (isCurrent) return;
                                    if (s === 'ordem gerada') {
                                        handleConvertToOS();
                                        return;
                                    }
                                    handleStatusChange(s);
                                }}
                                sx={{ gap: 1.25, fontSize: 13, fontWeight: 700, minWidth: 228 }}
                            >
                                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: toneDotColor(meta.tone), flexShrink: 0 }} />
                                {meta.label}
                                {isCurrent && <Check size={14} strokeWidth={2.5} style={{ marginLeft: 'auto' }} />}
                            </MenuItem>
                        );
                    })}
                </Menu>

                {/* Sem abas Financeiro/Checklist — orçamento só tem a lista de itens. */}
                <Typography sx={{ fontSize: 14, fontWeight: 800, mb: 1.75 }}>Itens do orçamento</Typography>

                {converted && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        p: '13px 16px',
                        borderRadius: '12px',
                        bgcolor: 'secondary.bg',
                        color: 'secondary.main',
                        fontSize: 12.5,
                        fontWeight: 600,
                        mb: 2,
                    }}>
                        <ArrowLeftRight size={16} strokeWidth={2} />
                        Este orçamento já teve a Ordem de Serviço gerada — os itens ficam travados para edição.
                    </Box>
                )}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.7fr 1fr' }, gap: 2, alignItems: 'flex-start' }}>
                    <Card elevation={0}>
                        <CardContent sx={{ p: 2 }}>
                            {!converted && canEditItems && (
                                <Box sx={{ mb: 2 }}>
                                    <DefaultAutocomplete
                                        variant="pos"
                                        handleGetSelected={handleGetSelected}
                                        handleChange={(e) => {
                                            handleSearchItem(e.target.value);
                                            setUiState(prev => ({ ...prev, searchProduct: e.target.value }));
                                        }}
                                        id="product-search"
                                        placeholder="Buscar produto ou serviço para adicionar…"
                                        inputValue={uiState.searchProduct}
                                        resetKey={uiState.autocompleteResetKey}
                                        options={itemsAutocomplete}
                                    />
                                </Box>
                            )}

                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={itemFilter}
                                onChange={(e, v) => v && setItemFilter(v)}
                                sx={{ mb: 2, ...(isMobileShell && { width: '100%', '& .MuiToggleButton-root': { flex: '1 1 0', minWidth: 0, whiteSpace: 'nowrap' } }) }}
                            >
                                <ToggleButton value="todos">{isMobileShell ? 'Todos' : 'Todos os itens'}</ToggleButton>
                                <ToggleButton value="product">{isMobileShell ? 'Produtos' : 'Apenas produtos'}</ToggleButton>
                                <ToggleButton value="service">{isMobileShell ? 'Serviços' : 'Apenas serviços'}</ToggleButton>
                            </ToggleButtonGroup>

                            <Box sx={{ maxHeight: 560, overflowY: 'auto', pr: 0.5 }}>
                                {loadingItems ? (
                                    <Stack spacing={1}>
                                        {[1, 2, 3].map((n) => (
                                            <Skeleton key={n} variant="rounded" height={68} sx={{ borderRadius: '12px' }} />
                                        ))}
                                    </Stack>
                                ) : filteredCart.length === 0 ? (
                                    <EmptyMiniState
                                        icon={Layers}
                                        title="Nenhum item neste orçamento"
                                        subtitle={!converted ? 'Busque um produto ou serviço acima para adicionar' : 'Este orçamento não possui itens registrados'}
                                    />
                                ) : (
                                    filteredCart.map((item) => {
                                        const lineId = item.item_id ?? item.id;
                                        return (
                                            <CartRow
                                                key={String(lineId)}
                                                item={item}
                                                editable={!converted && canEditItems}
                                                onIncrement={() => handleIncrementItem(lineId)}
                                                onDecrement={() => handleDecrementItem(lineId)}
                                                onDiscount={() => openProductItemDiscountDialog(item)}
                                                onRemove={() => handleDeleteItem(lineId)}
                                            />
                                        );
                                    })
                                )}
                            </Box>
                        </CardContent>
                    </Card>

                    <Card elevation={0} sx={{ position: 'sticky', top: 20 }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 1.5 }}>Resumo do orçamento</Typography>
                            {loadingValues ? (
                                <Stack spacing={1}>
                                    {[1, 2, 3, 4].map((n) => <Skeleton key={n} variant="text" height={28} />)}
                                </Stack>
                            ) : (
                                <>
                                    <SummaryRow label="Total de itens" value={quoteState.totalItems} />
                                    <SummaryRow label="Produtos" value={`R$ ${quoteState.total_products}`} />
                                    {hasProductItemDiscount && (
                                        <SummaryRow label="Desconto nos produtos" value={`− R$ ${quoteState.total_product_item_discount}`} tone="danger" />
                                    )}
                                    <SummaryRow label="Serviços" value={`R$ ${quoteState.total_services}`} />
                                    <Divider sx={{ my: 1 }} />
                                    <SummaryRow label="Total" value={`R$ ${quoteState.total}`} tone="success" big />
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Box>

                {customerMessages.length > 0 && (
                    <Card elevation={0} sx={{ mt: 2 }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 1.5 }}>Dúvidas do cliente</Typography>
                            <Stack spacing={1.25}>
                                {customerMessages.map((m) => (
                                    <Box key={m.id} sx={{ p: '10px 14px', borderRadius: '12px', bgcolor: 'action.hover' }}>
                                        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mb: 0.5 }}>
                                            {new Date(m.created_at).toLocaleString('pt-BR')}
                                        </Typography>
                                        <Typography sx={{ fontSize: 13.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.message}</Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>
                )}
            </Box>
            )}

            {/* MODAL: converter em ordem de serviço — reaproveitado tanto pelo
                botão "Converter em OS" quanto por escolher "Convertido" no
                menu de status. */}
            <DialogForm
                iconType={'info'}
                open={openConvertDialog}
                title={'Gerar Ordem de Serviço'}
                confirmButtonText={'Sim, Gerar'}
                content={'Tem certeza que deseja gerar uma Ordem de Serviço a partir deste orçamento?'}
                secondaryContent={'Esta ação criará uma nova Ordem de Serviço baseada neste orçamento. Os itens serão copiados e a edição deste orçamento será travada.'}
                onSubmit={confirmConvertToOS}
                onClose={() => setOpenConvertDialog(false)}
            />

            <EditQuoteModal
                open={openEditModal}
                onClose={() => setOpenEditModal(false)}
                quoteId={id}
                onSuccess={handleEditSuccess}
            />

            {/* MODAL: excluir orçamento */}
            <DialogForm
                iconType={'warning'}
                open={openDeleteDialog}
                title={'Excluir Orçamento'}
                confirmButtonText={'Confirmar Exclusão'}
                content={'Tem certeza que deseja excluir este orçamento? Esta ação não pode ser desfeita.'}
                onSubmit={confirmDeleteQuote}
                onClose={() => setOpenDeleteDialog(false)}
            />

            <QuoteWhatsappDialog
                open={openWhatsappModal}
                quoteId={id}
                quoteNumber={quoteNumber}
                onClose={() => setOpenWhatsappModal(false)}
                onSent={() => fetchQuoteDetails()}
                onResult={(msg, sev) => {
                    setMessage(msg);
                    setSeverity(sev);
                    setOpenSnackbar(true);
                }}
            />

            {/* MODAL: enviar orçamento por e-mail (`#emailModal`) — e-mail
                pré-preenchido a partir do cadastro do cliente, editável;
                mesmo endpoint/payload de antes (`POST .../send-email`). */}
            <DialogForm
                iconType={'info'}
                open={openEmailModal}
                title={'Enviar orçamento por e-mail'}
                content={'O cliente recebe um link para visualizar e aprovar o orçamento.'}
                confirmButtonText={'Enviar'}
                cancelButtonText={'Cancelar'}
                onSubmit={confirmSendEmail}
                onClose={() => {
                    if (!sendingEmail) setOpenEmailModal(false);
                }}
                loading={sendingEmail}
                loadingText="Enviando..."
            >
                <TextField
                    label="E-mail do destinatário"
                    fullWidth
                    variant="outlined"
                    sx={standaloneFieldSx}
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                    disabled={sendingEmail}
                    autoFocus
                />
                <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                    Pré-preenchido a partir do cadastro do cliente — edite se necessário.
                </Typography>
            </DialogForm>

            <DialogForm
                iconType={'info'}
                open={openGenericItemDialog}
                title={'Adicionar Item Genérico'}
                content={'Preencha os dados do item genérico'}
                confirmButtonText={'Adicionar Item'}
                cancelButtonText={'Cancelar'}
                onSubmit={handleAddGenericItem}
                onClose={handleCancelGenericItem}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                    <TextField
                        label="Nome do Item"
                        fullWidth
                        variant="outlined"
                        sx={standaloneFieldSx}
                        value={genericItemData.name}
                        onChange={(e) => setGenericItemData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Digite o nome do item"
                        required
                        autoFocus
                    />

                    <TextField
                        label="Preço"
                        fullWidth
                        variant="outlined"
                        sx={standaloneFieldSx}
                        value={genericItemData.price}
                        onChange={(e) => {
                            const value = e.target.value;
                            const formatted = formatCurrency(value);
                            setGenericItemData(prev => ({ ...prev, price: formatted }));
                        }}
                        placeholder="0,00"
                        required
                        InputProps={{
                            startAdornment: <Typography sx={{ mt: '14px', fontSize: '13.5px', color: 'text.secondary' }}>R$</Typography>
                        }}
                    />

                    <TextField
                        label="Quantidade"
                        fullWidth
                        variant="outlined"
                        sx={standaloneFieldSx}
                        type="number"
                        value={genericItemData.quantity}
                        onChange={(e) => {
                            const value = parseInt(e.target.value) || 1;
                            setGenericItemData(prev => ({ ...prev, quantity: value > 0 ? value : 1 }));
                        }}
                        inputProps={{ min: 1 }}
                        required
                    />
                </Box>
            </DialogForm>

            <DialogForm
                iconType="info"
                title="Desconto no produto"
                subtitle={itemDiscountDialog.item?.item_name || itemDiscountDialog.item?.name || ''}
                content="Percentual (ex.: 10%) ou valor fixo (ex.: 10,00). Diferente do desconto global do orçamento. Use Remover para limpar o desconto da linha."
                open={itemDiscountDialog.open}
                onClose={() => {
                    if (!itemDiscountDialog.loading) {
                        setItemDiscountDialog({ open: false, item: null, value: '', loading: false });
                    }
                }}
                onCancel={() => {
                    if (!itemDiscountDialog.loading) {
                        setItemDiscountDialog({ open: false, item: null, value: '', loading: false });
                    }
                }}
                onSubmit={() => handleSubmitQuoteProductItemDiscount(false)}
                confirmButtonText="Aplicar"
                cancelButtonText="Cancelar"
                loading={itemDiscountDialog.loading}
                loadingText="Aplicando..."
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <TextField
                        label="Desconto"
                        fullWidth
                        variant="outlined"
                        sx={standaloneFieldSx}
                        placeholder="10% ou 10,00"
                        disabled={itemDiscountDialog.loading}
                        value={itemDiscountDialog.value}
                        onChange={(e) =>
                            setItemDiscountDialog((prev) => ({ ...prev, value: e.target.value }))
                        }
                    />
                    <Button
                        type="button"
                        variant="text"
                        color="error"
                        size="small"
                        disabled={itemDiscountDialog.loading}
                        onClick={() => handleSubmitQuoteProductItemDiscount(true)}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}
                    >
                        Remover desconto
                    </Button>
                </Box>
            </DialogForm>
        </PageScaffold>
    );
}
